import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Play, Pause, Loader2, History, RotateCcw, Save } from "lucide-react";
import { api } from "@/lib/api";
import { ArtThumb } from "@/components/riptag/TrackTable";

const DECADE_OPTS = ["", "1960s", "1970s", "1980s", "1990s", "2000s", "2010s", "2020s"];
const KEYS = ["", "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B",
  "C Minor", "C# Minor", "D Minor", "D# Minor", "E Minor", "F Minor", "F# Minor",
  "G Minor", "G# Minor", "A Minor", "A# Minor", "B Minor"];
const FIELDS = ["title", "artist", "album", "album_artist", "year", "track_no", "genre", "genres",
  "decade", "bpm", "energy", "musical_key", "art_url", "rotation", "lib_status", "stations"];

export const TrackDetailsPanel = ({ trackId, stations, genres, onClose, onSaved }) => {
  const [track, setTrack] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const toForm = (t) => ({
    title: t.title || "", artist: t.artist || "", album: t.album || "",
    album_artist: t.album_artist || "", year: t.year || "", track_no: t.track_no || 0,
    original_year: t.original_year || "",
    genre: t.genre || "", genres: t.genres || [],
    decade: t.decade_manual ? t.decade : "",
    bpm: t.bpm ?? "", energy: t.energy ?? "", musical_key: t.musical_key || "",
    art_url: t.art_url || "", rotation: t.rotation || "unassigned",
    lib_status: t.lib_status || "active", stations: t.stations || [],
  });

  const loadDetail = async () => {
    try {
      const r = await api.get(`/music/tracks/${trackId}`);
      setTrack(r.data);
      setForm(toForm(r.data));
    } catch { toast.error("Couldn't load track"); onClose(); }
  };
  useEffect(() => { loadDetail(); audioRef.current?.pause(); setPlaying(false); }, [trackId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => audioRef.current?.pause(), []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleStation = (sid) => set("stations",
    form.stations.includes(sid) ? form.stations.filter((x) => x !== sid) : [...form.stations, sid]);
  const toggleGenre = (g) => set("genres",
    form.genres.includes(g) ? form.genres.filter((x) => x !== g) : [...form.genres, g].slice(0, 10));

  const save = async () => {
    setBusy("save");
    try {
      const body = {
        ...form,
        track_no: Number(form.track_no) || 0,
        bpm: form.bpm === "" ? 0 : Number(form.bpm),
        energy: form.energy === "" ? 0 : Number(form.energy),
      };
      await api.patch(`/music/tracks/${trackId}`, body);
      await loadDetail();
      toast.success("Changes saved — audio file untouched, metadata updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy("");
  };

  const restore = async () => {
    setBusy("restore");
    try {
      await api.post(`/music/tracks/${trackId}/restore`);
      await loadDetail();
      toast.success("Original metadata restored");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    setBusy("");
  };

  const play = async () => {
    const audio = audioRef.current || (audioRef.current = new Audio());
    if (playing) { audio.pause(); setPlaying(false); return; }
    setBusy("play");
    try {
      if (!audio.src || audio.dataset.tid !== trackId) {
        if (audio.src) URL.revokeObjectURL(audio.src);
        const r = await api.get(`/music/tracks/${trackId}/audio`, { responseType: "blob" });
        audio.src = URL.createObjectURL(r.data);
        audio.dataset.tid = trackId;
      }
      audio.onended = () => setPlaying(false);
      await audio.play();
      setPlaying(true);
    } catch { toast.error("Couldn't play this track"); }
    setBusy("");
  };

  if (!track) return <div className="rounded-xl border border-line bg-bg-surface p-6"><Loader2 size={16} className="animate-spin text-ink-muted" /></div>;

  const input = (label, key, props = {}) => (
    <div>
      <div className="type-card-meta-label text-ink-muted mb-1">{label}</div>
      <input value={form[key]} onChange={(e) => set(key, e.target.value)} {...props}
        data-testid={`ml-detail-${key}`}
        className="w-full rounded-md border border-line bg-bg-base px-2 py-1.5 text-xs text-ink-primary focus:border-brand-amber focus:outline-none" />
    </div>
  );

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4 sticky top-4" data-testid="ml-track-details">
      <div className="flex items-center justify-between mb-3">
        <h3 className="type-section-heading text-ink-primary">Track Details</h3>
        <button onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="ml-detail-close"><X size={15} /></button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <ArtThumb url={form.art_url} size="w-16 h-16" />
        <div className="min-w-0 flex-1">
          <div className="type-list-title text-ink-primary truncate">{track.title || "Untitled"}</div>
          <div className="type-list-subtitle text-ink-muted truncate">{track.artist || "Unknown artist"}</div>
          <div className="type-timestamp text-ink-muted">{track.decade} · {track.format?.toUpperCase()} · {Math.round(track.duration || 0)}s</div>
        </div>
        <button onClick={play} data-testid="ml-detail-play"
          className="w-9 h-9 rounded-full bg-brand-amber text-bg-base flex items-center justify-center shrink-0 hover:opacity-90">
          {busy === "play" ? <Loader2 size={14} className="animate-spin" /> : playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">{input("Title", "title")}</div>
        {input("Artist", "artist")}
        {input("Album Artist", "album_artist")}
        <div className="col-span-2">{input("Album", "album")}</div>
        {input("Year", "year", { placeholder: "e.g. 1973" })}
        {input("Original Year", "original_year", { placeholder: "first recording", title: "Original recording year — drives the decade, protects compilation tracks" })}
        {input("Track #", "track_no", { type: "number", min: 0 })}
        <div>
          <div className="type-card-meta-label text-ink-muted mb-1">Primary Genre</div>
          <input list="ml-genre-list" value={form.genre} onChange={(e) => set("genre", e.target.value)}
            data-testid="ml-detail-genre"
            className="w-full rounded-md border border-line bg-bg-base px-2 py-1.5 text-xs text-ink-primary focus:border-brand-amber focus:outline-none" />
          <datalist id="ml-genre-list">{genres.map((g) => <option key={g.name} value={g.name} />)}</datalist>
        </div>
        <div>
          <div className="type-card-meta-label text-ink-muted mb-1">Decade</div>
          <select value={form.decade} onChange={(e) => set("decade", e.target.value)} data-testid="ml-detail-decade"
            className="w-full rounded-md border border-line bg-bg-base px-2 py-1.5 text-xs text-ink-primary">
            {DECADE_OPTS.map((d) => {
              const y = String(form.original_year || form.year || "").slice(0, 4);
              const auto = /^(19|20)\d\d$/.test(y) ? `${Math.floor(+y / 10) * 10}s` : "Unclassified";
              return <option key={d} value={d}>{d === "" ? `Auto (${auto})` : d}</option>;
            })}
          </select>
        </div>
        <div>
          <div className="type-card-meta-label text-ink-muted mb-1 flex items-center gap-1">
            BPM
            {track.bpm_source === "analysis" && (
              <span className="rounded-full bg-cyan-500/15 text-cyan-300 px-1.5 text-[8px] font-mono" title="Measured by audio analysis">est</span>
            )}
            {track.analysis?.bpm_uncertain && (
              <span className="rounded-full bg-amber-500/15 text-amber-300 px-1.5 text-[8px] font-mono" title="Tempo detection was uncertain — live take, tempo changes or sparse rhythm">uncertain</span>
            )}
          </div>
          <div className="flex gap-1">
            <input type="number" min="0" max="400" value={form.bpm} data-testid="ml-detail-bpm"
              onChange={(e) => set("bpm", e.target.value)}
              className="w-full rounded-md border border-line bg-bg-base px-2 py-1.5 text-xs text-ink-primary" />
            <button onClick={() => form.bpm && set("bpm", Math.round(Number(form.bpm) / 2))} data-testid="ml-detail-bpm-half"
              title="Half-time" className="rounded-md border border-line px-1.5 text-[10px] font-bold text-ink-secondary hover:text-ink-primary">½×</button>
            <button onClick={() => form.bpm && Number(form.bpm) <= 200 && set("bpm", Math.round(Number(form.bpm) * 2))} data-testid="ml-detail-bpm-double"
              title="Double-time" className="rounded-md border border-line px-1.5 text-[10px] font-bold text-ink-secondary hover:text-ink-primary">2×</button>
          </div>
        </div>
        {input("Energy (0–100)", "energy", { type: "number", min: 0, max: 100 })}
        <div>
          <div className="type-card-meta-label text-ink-muted mb-1 flex items-center gap-1">
            Musical Key
            {track.musical_key_source === "analysis" && (
              <span className="rounded-full bg-cyan-500/15 text-cyan-300 px-1.5 text-[8px] font-mono" title="Estimated by audio analysis">est</span>
            )}
            {track.analysis?.key_uncertain && (
              <span className="rounded-full bg-amber-500/15 text-amber-300 px-1.5 text-[8px] font-mono" title="Key estimate was uncertain">uncertain</span>
            )}
          </div>
          <select value={form.musical_key} onChange={(e) => set("musical_key", e.target.value)} data-testid="ml-detail-key"
            className="w-full rounded-md border border-line bg-bg-base px-2 py-1.5 text-xs text-ink-primary">
            {KEYS.map((k) => <option key={k} value={k}>{k || "—"}</option>)}
          </select>
        </div>
        <div>
          <div className="type-card-meta-label text-ink-muted mb-1">Rotation</div>
          <select value={form.rotation} onChange={(e) => set("rotation", e.target.value)} data-testid="ml-detail-rotation"
            className="w-full rounded-md border border-line bg-bg-base px-2 py-1.5 text-xs text-ink-primary">
            {["unassigned", "heavy", "medium", "light"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <div className="type-card-meta-label text-ink-muted mb-1">Status</div>
          <select value={form.lib_status} onChange={(e) => set("lib_status", e.target.value)} data-testid="ml-detail-status"
            className="w-full rounded-md border border-line bg-bg-base px-2 py-1.5 text-xs text-ink-primary">
            {["active", "inactive", "review"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="col-span-2">{input("Artwork URL", "art_url", { placeholder: "https://…" })}</div>
      </div>

      <div className="mt-3">
        <div className="type-card-meta-label text-ink-muted mb-1">Additional Genres</div>
        <div className="flex flex-wrap gap-1" data-testid="ml-detail-genres">
          {genres.slice(0, 16).map((g) => (
            <button key={g.name} onClick={() => toggleGenre(g.name)}
              className={`rounded-full border px-2 py-0.5 text-[10px] transition ${
                form.genres.includes(g.name) ? "border-amber-500/60 bg-amber-500/15 text-brand-amber" : "border-line text-ink-muted hover:text-ink-primary"}`}>
              {g.name}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <div className="type-card-meta-label text-ink-muted mb-1">Station Assignment</div>
        <div className="space-y-1" data-testid="ml-detail-stations">
          {stations.map((s) => (
            <label key={s.station_id} className="flex items-center gap-2 cursor-pointer text-xs text-ink-secondary">
              <input type="checkbox" checked={form.stations.includes(s.station_id)}
                onChange={() => toggleStation(s.station_id)} className="accent-amber-500"
                data-testid={`ml-detail-station-${s.station_id}`} />
              {s.name}
            </label>
          ))}
        </div>
      </div>

      {track.analysis?.tech && (
        <div className="mt-3 rounded-lg border border-line/60 bg-bg-base p-2.5" data-testid="ml-detail-analysis">
          <div className="type-card-meta-label text-ink-muted mb-1.5">
            Audio Analysis <span className="normal-case">({(track.analysis.at || "").slice(0, 10)})</span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono text-ink-secondary">
            <span>Format: {track.analysis.tech.format || "—"}</span>
            <span>Bitrate: {track.analysis.tech.bitrate_kbps ? `${track.analysis.tech.bitrate_kbps} kbps` : "—"}</span>
            <span>Sample: {track.analysis.tech.sample_rate ? `${(track.analysis.tech.sample_rate / 1000).toFixed(1)} kHz` : "—"}</span>
            <span>Channels: {track.analysis.tech.channels || "—"}</span>
            <span>Loudness: {track.analysis.lufs != null ? `${track.analysis.lufs} LUFS` : "—"}</span>
            <span>True peak: {track.analysis.true_peak_db != null ? `${track.analysis.true_peak_db} dB` : "—"}</span>
            <span>Silence: {track.analysis.silence_sec ? `${track.analysis.silence_sec}s` : "none"}</span>
            <span>Duration: {track.analysis.tech.duration ? `${Math.round(track.analysis.tech.duration)}s` : "—"}</span>
          </div>
          {(track.analysis.defects || []).length > 0 && (
            <div className="mt-1.5 text-[10px] text-rose-300" data-testid="ml-detail-defects">
              ⚠ {(track.analysis.defects || []).join(" · ")}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button onClick={save} disabled={!!busy} data-testid="ml-detail-save"
          className="type-button-label flex-1 flex items-center justify-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base disabled:opacity-50">
          {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Changes
        </button>
        <button onClick={() => setForm(toForm(track))} disabled={!!busy} data-testid="ml-detail-cancel"
          className="type-button-label rounded-md border border-line px-3 py-2 text-ink-primary hover:bg-white/5 disabled:opacity-50">
          Cancel
        </button>
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button onClick={() => setShowHistory((v) => !v)} data-testid="ml-detail-history-toggle"
          className="type-timestamp flex items-center gap-1 text-ink-muted hover:text-ink-primary">
          <History size={11} /> History ({(track.audit || []).length})
        </button>
        {track.has_original && (
          <button onClick={restore} disabled={!!busy} data-testid="ml-detail-restore"
            className="type-timestamp flex items-center gap-1 text-ink-muted hover:text-brand-amber disabled:opacity-50">
            <RotateCcw size={11} /> Restore original
          </button>
        )}
      </div>
      {showHistory && (
        <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5" data-testid="ml-detail-history">
          {(track.audit || []).length === 0 && <div className="type-timestamp text-ink-muted">No edits yet.</div>}
          {(track.audit || []).map((a, i) => (
            <div key={i} className="rounded border border-line/40 px-2 py-1.5">
              <div className="type-timestamp text-ink-muted">{(a.at || "").slice(0, 16).replace("T", " ")} · {a.by}</div>
              <div className="text-[10px] text-ink-secondary">
                {Object.entries(a.changes || {}).map(([k, [o, n]]) => `${k}: "${o}" → "${n}"`).join(" · ")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
